import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { useParams, useLocation } from 'react-router-dom';
import { strings } from '/imports/strings';
import { Checkbox } from '/imports/ui/Checkbox';
import { ModelFormPage } from '/imports/ui/ModelFormPage';
import { Select } from '/imports/ui/Select';
import { Button } from '/imports/ui/Button';
import { Page } from '/imports/ui/Page';
import { modelClass, ProductionMeter } from '/imports/api/productionMeters';
import { Input } from '/imports/ui/Input';
import { formatDateTime } from '/imports/utils';
import { FormField } from '/imports/ui/FormField';
import { DateTimeInput } from '/imports/ui/DateTimeInput';
import { timezoneOptions } from '/imports/options';
import { NumberInput } from '/imports/ui/NumberInput';
import { TextArea } from '/imports/ui/TextArea';
import { PullProductionIntervalsModal } from '/imports/ui/PullProductionIntervalsModal';
import { Modal } from '/imports/ui/Modal';
import { ProductionIntervalsVsTimeChart } from '/imports/ui/ProductionIntervalsVsTimeChart';
import { AddressInput } from '/imports/ui/AddressInput';

const ProductionMeterPageStyled = styled(Page)`
`;

/** Form page for viewing and editing a single production meter. */
export const ProductionMeterPage = () => {
  const { _id } = useParams<any>();
  const location = useLocation();
  const presetType = new URLSearchParams(location.search).get('type') as ProductionMeter['type'] | null;
  const defaultUpdate: Partial<ProductionMeter> = _id === 'new' && presetType === 'dummy'
    ? { _id, active: true, type: 'dummy', identifier: `dmy-${Date.now()}` }
    : { _id, active: true };
  const [update, setUpdate] = useState<Partial<ProductionMeter>>(defaultUpdate);
  const documentRef = useRef<ProductionMeter>(undefined);
  const doc = documentRef.current;
  const [showPullModal, setShowPullModal] = useState(false);
  const [showProductionModal, setShowProductionModal] = useState(false);
  return <ModelFormPage
    modelClass={modelClass}
    styled={ProductionMeterPageStyled}
    permission={(user) => user?.isSuperUser}
    documentRef={documentRef}
    defaultUpdate={defaultUpdate}
    update={update}
    setUpdate={setUpdate}
    formSpec={[
      [{ field: 'active', input: { type: Checkbox } }],
      [{ field: 'name' }, { field: 'identifier', input: { type: Input, props: { disabled: true } } }],
      [{ field: 'address', input: { type: AddressInput } }],
      [{ field: 'formattedAddressOverride' }],
      [
        { field: 'timezone', input: { type: Select, props: { options: [{ value: '', label: '' }, ...timezoneOptions()] } } },
        { field: 'commissioningDate', input: { type: DateTimeInput, props: { zone: update.timezone || doc?.timezone } } },
        { field: 'size', label: strings.SystemSize(), input: { type: NumberInput } },
      ],
      [
        { field: 'billingAccountNumber' }, { field: 'billingEmail' }, { field: 'customerName' },
        { field: 'type', input: { type: Input, props: { disabled: true } } },
      ],
      [
        { field: 'ppaRate', input: { type: NumberInput, props: { min: '0', step: '0.0001' } } },
        { field: 'ppaEscalator', label: `${strings.PpaEscalator()} (%)`, input: { type: NumberInput, props: { min: '0', step: '0.0001' } } },
        { field: 'fixedPayment', input: { type: NumberInput, props: { min: '0', step: '0.001' } } },
        { field: 'degradationRate', label: `${strings.DegradationRate()} (%)`, input: { type: NumberInput, props: { min: '0', step: '0.0001' } } },
      ],
      [{
        field: 'forecastedProduction', input: (setUpdate, update, document) => (
          <FormField key="forecastedProduction" field="forecastedProduction">
            <label>{strings.ForecastedProduction()}</label>
            <TextArea
              value={update.forecastedProduction === undefined ? document?.forecastedProduction?.join(', ') : update.forecastedProduction?.join(', ')}
              onChange={(text) => setUpdate({ ...update, forecastedProduction: text.split(',').map((t) => parseFloat(t.trim())) })}
            />
          </FormField>
        )
      }],
      [
        {
          field: 'createdAt', input: () => (
            <FormField key="createdAt" field="createdAt"><label>{strings.CreatedAt()}</label><Input placeholder={formatDateTime(doc?.createdAt)} disabled /></FormField>
          )
        }
      ],

    ]}
    extraButtons={
      <>
        <Button onClick={() => setShowProductionModal(true)}>{strings.ViewProduction()}</Button>
        <Button onClick={() => setShowPullModal(true)}>{strings.PullProduction()}</Button>
      </>
    }
    footer={
      <div>
        {
          showPullModal && doc ?
            <PullProductionIntervalsModal meterIds={[doc._id]} onClose={() => setShowPullModal(false)} />
            :
            null
        }
        {
          showProductionModal && doc ?
            <ProductionModal onClose={() => setShowProductionModal(false)}
              body={<ProductionIntervalsVsTimeChart meter={{ ...doc, ...update }} />}
            />
            :
            null
        }
      </div>
    }
  />;
};

const ProductionModal = styled(Modal)`
  .modal-window {
    width: 85rem;
    max-width: 100%;
    .recharts-responsive-container {
      height: 18rem !important;
    }
  }
`;